import { useState, useEffect, useRef } from 'react';
import { Download, Lock, Unlock, X } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { Dropzone } from '@/components/ui/Dropzone';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { formatBytes, loadImage, readFileAsDataURL } from '@/lib/utils';
import { processAndSave, canvasToBlob, stripExt } from '@/lib/bulk';

const PRESETS = [
  { label: 'Original', w: 0, h: 0 },
  { label: 'Twitter post', w: 1200, h: 675 },
  { label: 'Twitter banner', w: 1500, h: 500 },
  { label: 'LinkedIn post', w: 1200, h: 627 },
  { label: 'LinkedIn cover', w: 1584, h: 396 },
  { label: 'Instagram square', w: 1080, h: 1080 },
  { label: 'Instagram story', w: 1080, h: 1920 },
  { label: 'YouTube thumb', w: 1280, h: 720 },
  { label: 'Open Graph', w: 1200, h: 630 },
  { label: 'Favicon', w: 256, h: 256 },
];

export function ImageResize() {
  const { pendingFile, setPendingFile } = useApp();
  const [files, setFiles] = useState<File[]>([]);
  const [previewImg, setPreviewImg] = useState<HTMLImageElement | null>(null);
  const [w, setW] = useState(0);
  const [h, setH] = useState(0);
  const [origW, setOrigW] = useState(0);
  const [origH, setOrigH] = useState(0);
  const [locked, setLocked] = useState(true);
  const [format, setFormat] = useState<'png' | 'jpeg' | 'webp'>('png');
  const [working, setWorking] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Pick up file routed by drop overlay
  useEffect(() => {
    if (pendingFile && pendingFile.type.startsWith('image/')) {
      handleFiles([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFiles(incoming: File[]) {
    if (!incoming.length) return;
    const next = [...files, ...incoming.filter((f) => f.type.startsWith('image/'))];
    setFiles(next);

    // Use the first file as the live preview / dimension reference
    if (!previewImg && next[0]) {
      const url = await readFileAsDataURL(next[0]);
      const im = await loadImage(url);
      setPreviewImg(im);
      setOrigW(im.width);
      setOrigH(im.height);
      setW(im.width);
      setH(im.height);
    }
  }

  function removeFile(i: number) {
    const next = files.filter((_, idx) => idx !== i);
    setFiles(next);
    if (next.length === 0) {
      setPreviewImg(null);
      setOrigW(0);
      setOrigH(0);
    } else if (i === 0) {
      // Reload preview from the new first file
      readFileAsDataURL(next[0]).then(loadImage).then((im) => {
        setPreviewImg(im);
        setOrigW(im.width);
        setOrigH(im.height);
        setW(im.width);
        setH(im.height);
      });
    }
  }

  // Render canvas when preview image or size changes
  useEffect(() => {
    if (!previewImg || !canvasRef.current || !w || !h) return;
    const c = canvasRef.current;
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(previewImg, 0, 0, w, h);
  }, [previewImg, w, h]);

  function setNewW(nw: number) {
    setW(nw);
    if (locked && origW) setH(Math.round((nw * origH) / origW));
  }
  function setNewH(nh: number) {
    setH(nh);
    if (locked && origH) setW(Math.round((nh * origW) / origH));
  }

  async function exportAll() {
    if (files.length === 0) return;
    setWorking(true);
    setProgress({ done: 0, total: files.length });
    try {
      const ext = format === 'jpeg' ? 'jpg' : format;
      await processAndSave(
        files,
        async (file) => {
          const url = await readFileAsDataURL(file);
          const im = await loadImage(url);
          // Compute target dimensions for THIS image: if user set explicit w/h
          // and they match preview's original size, treat as "original" (keep
          // each image's own dims). Otherwise enforce the chosen dimensions.
          let tw = w;
          let th = h;
          if (tw === origW && th === origH) {
            tw = im.width;
            th = im.height;
          } else if (locked) {
            // Maintain aspect ratio per-file using the requested width.
            tw = w;
            th = Math.round((w * im.height) / im.width);
          }
          const c = document.createElement('canvas');
          c.width = tw;
          c.height = th;
          const ctx = c.getContext('2d')!;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(im, 0, 0, tw, th);
          const blob = await canvasToBlob(c, `image/${format}`, 0.92);
          return {
            blob,
            filename: `${stripExt(file.name)}-${tw}x${th}.${ext}`,
          };
        },
        {
          zipName: 'resized',
          onProgress: (done, total) => setProgress({ done, total }),
        },
      );
    } finally {
      setWorking(false);
      setProgress({ done: 0, total: 0 });
    }
  }

  return (
    <div>
      <ToolHeader
        toolId="image-resize"
        title="Image resize"
        description="Resize images with social-platform presets, aspect-ratio lock, and PNG/JPEG/WebP export. Drop multiple files for bulk export."
        badges={
          files.length > 1 ? <Badge variant="accent">{files.length} files</Badge> : null
        }
      />

      {files.length === 0 ? (
        <Dropzone
          accept="image/*"
          multiple
          onFiles={handleFiles}
          hint="PNG, JPG, WebP, GIF, AVIF — drop one or many"
          title="Drop or pick images"
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:order-2 space-y-4">
            <Field label="Width">
              <Input
                type="number"
                value={w}
                onChange={(e) => setNewW(Number(e.target.value) || 0)}
              />
            </Field>
            <div className="flex items-center justify-center -my-1">
              <button
                onClick={() => setLocked((l) => !l)}
                className="size-7 rounded text-fg-muted hover:bg-surface-hover transition"
              >
                {locked ? <Lock size={12} /> : <Unlock size={12} />}
              </button>
            </div>
            <Field label="Height">
              <Input
                type="number"
                value={h}
                onChange={(e) => setNewH(Number(e.target.value) || 0)}
              />
            </Field>

            <div className="border-t border-border pt-3">
              <Field label="Format">
                <Select
                  value={format}
                  onChange={(e) => setFormat(e.target.value as typeof format)}
                  options={[
                    { value: 'png', label: 'PNG · lossless' },
                    { value: 'jpeg', label: 'JPEG · smaller' },
                    { value: 'webp', label: 'WebP · modern' },
                  ]}
                />
              </Field>
            </div>

            <div>
              <div className="text-xs text-fg-muted mb-2">Presets</div>
              <div className="grid grid-cols-2 gap-1.5">
                {PRESETS.map((p) => (
                  <button
                    key={p.label}
                    onClick={() => {
                      if (p.w === 0) {
                        setW(origW);
                        setH(origH);
                      } else {
                        setW(p.w);
                        setH(p.h);
                      }
                    }}
                    className="px-2.5 py-1.5 text-2xs rounded-md border border-border hover:border-accent hover:bg-accent/5 transition text-left"
                  >
                    <div className="font-medium">{p.label}</div>
                    {p.w > 0 && <div className="text-fg-subtle">{p.w}×{p.h}</div>}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setFiles([]);
                  setPreviewImg(null);
                }}
              >
                Clear
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={working}
                onClick={exportAll}
              >
                <Download size={11} />
                {files.length > 1 ? `Export ${files.length}` : 'Export'}
              </Button>
            </div>
            {working && progress.total > 1 && (
              <div className="text-2xs text-fg-subtle">
                Processing {progress.done} / {progress.total}…
              </div>
            )}
          </Card>

          <Card className="lg:col-span-2 lg:order-1 !p-0 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border flex items-center justify-between text-xs">
              <span className="font-medium text-fg-muted">
                Preview {files.length > 1 && <span className="text-fg-subtle">· first of {files.length}</span>}
              </span>
              <div className="flex items-center gap-3 text-fg-subtle">
                <span>Original {origW}×{origH}</span>
                <span>·</span>
                <span>{files[0] && formatBytes(files[0].size)}</span>
              </div>
            </div>
            <div className="p-6 bg-bg-inset min-h-[360px] flex items-center justify-center">
              <canvas ref={canvasRef} className="max-w-full max-h-[420px] rounded-md shadow-card" />
            </div>
            {files.length > 1 && (
              <div className="border-t border-border px-3 py-2">
                <div className="flex flex-wrap gap-1.5">
                  {files.map((f, i) => (
                    <div
                      key={`${f.name}-${i}`}
                      className="flex items-center gap-1 pl-2 pr-1 py-0.5 text-2xs rounded-md bg-bg-inset border border-border max-w-[200px]"
                    >
                      <span className="truncate">{f.name}</span>
                      <button
                        onClick={() => removeFile(i)}
                        className="shrink-0 size-4 rounded hover:bg-surface-hover text-fg-subtle hover:text-danger flex items-center justify-center"
                      >
                        <X size={9} />
                      </button>
                    </div>
                  ))}
                  <label className="flex items-center gap-1 px-2 py-0.5 text-2xs rounded-md border border-dashed border-border hover:border-accent hover:bg-accent/5 cursor-pointer">
                    + add more
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => {
                        const list = e.target.files;
                        if (!list || !list.length) return;
                        handleFiles(Array.from(list));
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

export default ImageResize;
